import React, { createContext, useContext, useEffect, useState } from "react";
import type {
  BillingDocument,
  BillingState,
  Client,
  CompanySettings,
  DocumentStatus,
  DocumentType,
  Expense,
  Service,
  ToastMessage,
  View,
} from "../types";
import {
  blankDocument,
  loadPersistedState,
  persistState,
  totals,
  uid,
} from "../lib/utils";
import { demoState, emptyState } from "../data/defaults";

interface BillingContextValue {
  state: BillingState;
  view: View;
  setView: (view: View) => void;
  activeDoc: BillingDocument | null;
  setActiveDoc: (doc: BillingDocument | null) => void;
  previewDoc: BillingDocument | null;
  setPreviewDoc: (doc: BillingDocument | null) => void;
  activeForm: { kind: "client" | "service" | "expense"; value: any } | null;
  setActiveForm: (form: { kind: "client" | "service" | "expense"; value: any } | null) => void;
  toasts: ToastMessage[];
  addToast: (title: string, message?: string, type?: ToastMessage["type"]) => void;
  removeToast: (id: string) => void;
  openNewDocument: (type: DocumentType) => void;
  saveDocument: (doc: BillingDocument, markSent?: boolean) => void;
  deleteDocument: (doc: BillingDocument) => void;
  convertQuoteToInvoice: (quote: BillingDocument) => void;
  updateDocumentStatus: (id: string, type: DocumentType, status: DocumentStatus) => void;
  saveClient: (client: Client) => void;
  deleteClient: (id: string) => void;
  saveService: (service: Service) => void;
  deleteService: (id: string) => void;
  saveExpense: (expense: Expense) => void;
  deleteExpense: (id: string) => void;
  updateSettings: (settings: CompanySettings) => void;
  loadDemoData: () => void;
  resetData: () => void;
}

const BillingContext = createContext<BillingContextValue | null>(null);

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<BillingState>(() => loadPersistedState());
  const [view, setView] = useState<View>("dashboard");
  const [activeDoc, setActiveDoc] = useState<BillingDocument | null>(null);
  const [previewDoc, setPreviewDoc] = useState<BillingDocument | null>(null);
  const [activeForm, setActiveForm] = useState<{ kind: "client" | "service" | "expense"; value: any } | null>(null);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Automatically persist changes to LocalStorage
  useEffect(() => {
    persistState(state);
  }, [state]);

  const addToast = (title: string, message?: string, type: ToastMessage["type"] = "success") => {
    const id = uid();
    setToasts((prev) => [...prev, { id, title, message, type }]);
    setTimeout(() => {
      removeToast(id);
    }, 4000);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const openNewDocument = (type: DocumentType) => {
    setActiveDoc(blankDocument(type, state));
  };

  const saveDocument = (incomingDoc: BillingDocument, markSent = false) => {
    const calculated = totals(incomingDoc.items, incomingDoc.gstRate);
    const updated: BillingDocument = {
      ...incomingDoc,
      ...calculated,
      status: markSent ? "sent" : incomingDoc.status,
    };

    setState((prev) => {
      const isInvoice = updated.type === "invoice";
      const key = isInvoice ? "invoices" : "quotes";
      const list = [...prev[key]];
      const index = list.findIndex((x) => x.id === updated.id);

      if (index >= 0) {
        list[index] = updated;
      } else {
        list.unshift(updated);
      }

      return { ...prev, [key]: list };
    });

    setActiveDoc(null);
    addToast(
      `${updated.type === "invoice" ? "Invoice" : "Quotation"} ${updated.number} saved`,
      markSent ? "Marked as sent and ready for client delivery." : "Saved to local workspace."
    );
  };

  const deleteDocument = (doc: BillingDocument) => {
    setState((prev) => {
      const key = doc.type === "invoice" ? "invoices" : "quotes";
      return {
        ...prev,
        [key]: prev[key].filter((x) => x.id !== doc.id),
      };
    });
    addToast(`Deleted ${doc.number}`, undefined, "info");
  };

  const convertQuoteToInvoice = (quote: BillingDocument) => {
    const invoiceNumber = blankDocument("invoice", state).number;
    const newInvoice: BillingDocument = {
      ...quote,
      id: uid(),
      type: "invoice",
      number: invoiceNumber,
      status: "draft",
      sourceQuoteId: quote.id,
      date: new Date().toISOString().slice(0, 10),
      due: new Date().toISOString().slice(0, 10),
      createdAt: new Date().toISOString(),
    };

    setState((prev) => ({
      ...prev,
      invoices: [newInvoice, ...prev.invoices],
      quotes: prev.quotes.map((q) =>
        q.id === quote.id ? { ...q, status: "accepted", convertedInvoiceId: newInvoice.id } : q
      ),
    }));

    addToast(`Converted ${quote.number} to ${newInvoice.number}`, "Invoice created as draft.");
    setActiveDoc(newInvoice);
  };

  const updateDocumentStatus = (id: string, type: DocumentType, status: DocumentStatus) => {
    setState((prev) => {
      const key = type === "invoice" ? "invoices" : "quotes";
      return {
        ...prev,
        [key]: prev[key].map((d) => (d.id === id ? { ...d, status } : d)),
      };
    });
    addToast(`Status updated to ${status}`);
  };

  const saveClient = (client: Client) => {
    setState((prev) => {
      const list = [...prev.clients];
      const index = list.findIndex((c) => c.id === client.id);
      if (index >= 0) {
        list[index] = client;
      } else {
        list.unshift({ ...client, id: client.id || uid() });
      }
      return { ...prev, clients: list };
    });
    setActiveForm(null);
    addToast(`Client ${client.company || client.name} saved`);
  };

  const deleteClient = (id: string) => {
    setState((prev) => ({
      ...prev,
      clients: prev.clients.filter((c) => c.id !== id),
    }));
    addToast("Client removed", undefined, "info");
  };

  const saveService = (service: Service) => {
    setState((prev) => {
      const list = [...prev.services];
      const index = list.findIndex((s) => s.id === service.id);
      if (index >= 0) {
        list[index] = service;
      } else {
        list.unshift({ ...service, id: service.id || uid() });
      }
      return { ...prev, services: list };
    });
    setActiveForm(null);
    addToast(`Service "${service.name}" updated`);
  };

  const deleteService = (id: string) => {
    setState((prev) => ({
      ...prev,
      services: prev.services.filter((s) => s.id !== id),
    }));
    addToast("Service deleted", undefined, "info");
  };

  const saveExpense = (expense: Expense) => {
    setState((prev) => {
      const list = [...prev.expenses];
      const index = list.findIndex((e) => e.id === expense.id);
      if (index >= 0) {
        list[index] = expense;
      } else {
        list.unshift({ ...expense, id: expense.id || uid() });
      }
      return { ...prev, expenses: list };
    });
    setActiveForm(null);
    addToast(`Expense saved`);
  };

  const deleteExpense = (id: string) => {
    setState((prev) => ({
      ...prev,
      expenses: prev.expenses.filter((e) => e.id !== id),
    }));
    addToast("Expense record removed", undefined, "info");
  };

  const updateSettings = (settings: CompanySettings) => {
    setState((prev) => ({
      ...prev,
      settings,
    }));
    addToast("Company settings saved");
  };

  const loadDemoData = () => {
    const cloned = structuredClone(demoState);
    setState(cloned);
    persistState(cloned);
    addToast("Loaded authentic demo dataset", "All records refreshed.");
  };

  const resetData = () => {
    const empty = structuredClone(emptyState);
    setState(empty);
    persistState(empty);
    addToast("Workspace cleared", "Started with a blank slate.", "warning");
  };

  return (
    <BillingContext.Provider
      value={{
        state,
        view,
        setView,
        activeDoc,
        setActiveDoc,
        previewDoc,
        setPreviewDoc,
        activeForm,
        setActiveForm,
        toasts,
        addToast,
        removeToast,
        openNewDocument,
        saveDocument,
        deleteDocument,
        convertQuoteToInvoice,
        updateDocumentStatus,
        saveClient,
        deleteClient,
        saveService,
        deleteService,
        saveExpense,
        deleteExpense,
        updateSettings,
        loadDemoData,
        resetData,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => {
  const ctx = useContext(BillingContext);
  if (!ctx) {
    throw new Error("useBilling must be used within BillingProvider");
  }
  return ctx;
};
